import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth, API } from '../context/AuthContext';
import { CircleNotch, CheckCircle, ArrowRight, Lock } from '@phosphor-icons/react';

/**
 * /team/accept?token=... — magic-link landing page for an invited seat.
 *
 * Flow:
 *   1. User clicks invite link in their inbox.
 *   2. If not signed in, redirect to landing for Google auth (with `return`).
 *   3. POST {token} to /api/b2b/team/accept; on success show celebration
 *      and route to /team.
 */
export default function InviteAcceptTeam() {
    const { user, loading } = useAuth();
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('init'); // init | accepting | done | error
    const [errorMsg, setErrorMsg] = useState('');
    const token = params.get('token');

    useEffect(() => {
        if (loading) return;
        if (!token) {
            setStatus('error');
            setErrorMsg('No invite token found in link.');
            return;
        }
        if (!user) {
            // Bounce to landing with a return URL so the auth callback
            // brings them straight back to /team/accept?token=...
            const url = `${window.location.pathname}${window.location.search}`;
            sessionStorage.setItem('defectr:return_to', url);
            toast('Sign in with Google to accept your invite.');
            navigate('/');
            return;
        }
        setStatus('accepting');
        axios
            .post(`${API}/b2b/team/accept`, { token }, { withCredentials: true })
            .then((res) => {
                toast.success(`Welcome to ${res.data.team_name} 🎉`);
                setStatus('done');
                setTimeout(() => navigate('/team'), 1500);
            })
            .catch((err) => {
                setStatus('error');
                const detail = err?.response?.data?.detail;
                setErrorMsg(typeof detail === 'string' ? detail : 'We could not accept this invite.');
            });
    }, [user, loading, token, navigate]);

    return (
        <div className="min-h-screen bg-white flex items-center justify-center px-4" data-testid="invite-accept-page">
            <div className="max-w-md w-full border-2 border-[#18181B] p-8 text-center">
                {status === 'init' || status === 'accepting' ? (
                    <>
                        <CircleNotch size={32} weight="bold" className="animate-spin mx-auto" color="#1FA5CF" />
                        <h1 className="font-display text-2xl font-bold mt-4">Accepting your invite…</h1>
                        <p className="mt-2 text-sm text-[#52525B]">Just a sec while we link your account.</p>
                    </>
                ) : status === 'done' ? (
                    <>
                        <CheckCircle size={36} weight="fill" color="#00875A" className="mx-auto" />
                        <h1 className="font-display text-2xl font-bold mt-4">You're in!</h1>
                        <p className="mt-2 text-sm text-[#52525B]">Taking you to your team dashboard…</p>
                    </>
                ) : (
                    <>
                        <Lock size={32} weight="duotone" color="#E53935" className="mx-auto" />
                        <h1 className="font-display text-2xl font-bold mt-4">Couldn't accept invite</h1>
                        <p className="mt-2 text-sm text-[#52525B]">{errorMsg}</p>
                        <Link
                            to="/app"
                            data-testid="invite-accept-home-link"
                            className="mt-6 inline-flex items-center gap-2 underline decoration-[#1FA5CF] underline-offset-4 font-mono text-xs uppercase tracking-[0.2em]"
                        >
                            Go to DefectR <ArrowRight size={14} weight="bold" />
                        </Link>
                    </>
                )}
            </div>
        </div>
    );
}
